import { useParams, Navigate, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import {
  ArrowLeft,
  
  ArrowUpRight,
  CalendarDays,
  Thermometer,
  Sun,
  Backpack,
  
  PartyPopper,
  Footprints,
  CloudRain,
  Plus,
} from "lucide-react";
import { Seo, PageSchema } from "@/components/Seo";
import { ShareBar } from "@/components/ShareBar";
import { ExploreLinks } from "@/components/ExploreLinks";
import { GygTourBand } from "@/components/GetYourGuide";
import { Reveal } from "@/components/motion";
import { MONTH_PAGES, MONTH_SLUGS, monthBySlug } from "@/data/months";
import { GygTivoliCard } from "@/components/GetYourGuide";
import { MONTH_INFO } from "@/data/planner";
import { WeatherBadge } from "@/components/WeatherBadge";

// One user-chosen affiliate tour per month (GYG tour ids; partner id HIOKDT8 is
// set on the widget loader). Months not listed fall back to the canal tour.
const MONTH_TOURS = {
  1: { tourId: "727418", label: "THE HOME OF CARLSBERG EXPERIENCE" },
  2: { tourId: "1018384", label: "INSIDE THE ROYAL DANISH THEATRE" },
  5: { tourId: "616173", label: "THE CHRISTIANIA & CHRISTIANSHAVN WALK" },
  6: { tourId: "888258", label: "SEE THE CITY FROM THE WATER" },
  7: { tourId: "1308094", label: "THE PRIVATE LUXURY BOAT TOUR" },
  8: { tourId: "888258", label: "SEE THE CITY FROM THE WATER" },
  9: { tourId: "1297085", label: "THE GUIDED BIKE TOUR WITH CHRISTIANIA" },
  10: { tourId: "741740", label: "CHRISTIANSBORG PALACE TICKETS" },
  11: { tourId: "520149", label: "TASTE A DANISH CHRISTMAS" },
  12: { tourId: "1115932", label: "TIVOLI AT CHRISTMAS — SKIP THE QUEUE" },
};
const tourFor = (num) => MONTH_TOURS[num] || { tourId: "37848", label: "THE CLASSIC CANAL TOUR — FROM GAMMEL STRAND" };

export default function MonthPage() {
  const { month } = useParams();
  const m = monthBySlug(month);
  if (!m) {
    return <Navigate to={`/copenhagen-in/${MONTH_SLUGS[new Date().getMonth()]}`} replace />;
  }

  const w = MONTH_INFO[m.num];
  const prev = MONTH_PAGES[(m.num + 10) % 12];
  const next = MONTH_PAGES[m.num % 12];
  const tour = tourFor(m.num);

  // WHAT'S ON in date order — dated events first (chronological), undated after
  const events = [...m.events].sort((a, b) =>
    (a.start || "9999") < (b.start || "9999") ? -1 : (a.start || "9999") > (b.start || "9999") ? 1 : 0
  );

  const title = `Copenhagen in ${m.name} — Weather, What's On & Things to Do`;
  const description = `${m.intro.split(". ")[0]}. Weather, daylight, what to pack, events and things to do — plus the honest local verdict on whether ${m.name} is the best time to visit Copenhagen.`;
  const path = `/copenhagen-in/${m.slug}`;

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: m.faq.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };

  // Event rich snippets for the dated events (2026/2027 dates live in months.js).
  // Google's validator nags about four optional fields — fill them honestly:
  // the month's hero as image, the organising body where it's obvious from the
  // name, "Various artists" for festivals/city-wide happenings, and a 0-kr
  // offer only for genuinely free events (ticketed ones link their official
  // page instead of inventing a price).
  const ORGANIZERS = [
    ["Tivoli", "Tivoli"],
    ["Bakken", "Bakken"],
    ["CPH:DOX", "CPH:DOX"],
    ["Roskilde", "Roskilde Festival"],
    ["Light Festival", "Copenhagen Light Festival"],
    ["Vinterjazz", "Jazz Danmark"],
    ["Jazz Festival", "Jazz Danmark"],
    ["Distortion", "Distortion"],
    ["Copenhell", "Copenhell"],
    ["Pride", "Copenhagen Pride"],
    ["Royal Run", "Royal Run"],
    ["Coffee Festival", "Danish Coffee Festival"],
    ["Marathon", "Sparta — Copenhagen Marathon"],
    ["CopenHill", "CopenHill"],
    ["Golden Days", "Golden Days"],
    ["Fashion Week", "Copenhagen Fashion Week"],
    ["Kulturnatten", "Kulturnatten — Culture Night"],
    ["Hubertus", "The Hubertus Hunt, Dyrehaven"],
  ];
  const orgFor = (name) => {
    const hit = ORGANIZERS.find(([k]) => name.includes(k));
    return hit ? hit[1] : "Various venues across Copenhagen";
  };
  const FREE_EVENT = /Light Festival|Fastelavn|public holidays|Cherry blossoms|Liberation candles|Sankt Hans|Pride|Christmas markets|Lucia|New Year's Eve|Autumn break/i;
  const pageUrl = `https://cphhighlights.com${path}`;
  const eventSchema = m.events.some((e) => e.start)
    ? {
        "@context": "https://schema.org",
        "@graph": m.events
          .filter((e) => e.start)
          .map((e) => ({
            "@type": "Event",
            name: e.name,
            startDate: e.start,
            ...(e.end ? { endDate: e.end } : {}),
            eventStatus: "https://schema.org/EventScheduled",
            eventAttendanceMode: "https://schema.org/OfflineEventAttendanceMode",
            image: [m.img],
            location: {
              "@type": "Place",
              name: /Bakken|Hubertus|Hermitage/.test(e.name) ? "Dyrehaven, Klampenborg" : "Copenhagen",
              address: { "@type": "PostalAddress", addressLocality: "Copenhagen", addressCountry: "DK" },
            },
            organizer: { "@type": "Organization", name: orgFor(e.name) },
            performer: { "@type": "PerformingGroup", name: "Various artists" },
            offers: FREE_EVENT.test(e.name)
              ? { "@type": "Offer", price: "0", priceCurrency: "DKK", availability: "https://schema.org/InStock", url: e.url || pageUrl }
              : { "@type": "Offer", availability: "https://schema.org/InStock", url: e.url || pageUrl },
            ...(e.url ? { url: e.url } : {}),
            description: e.note,
          })),
      }
    : null;

  return (
    <main data-testid="month-page" className="pt-16 md:pt-20">
      <Seo title={title} description={description} path={path} image={m.ogImage} />
      <PageSchema name={title} description={description} path={path} crumb={`Copenhagen in ${m.name}`} />
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
        {eventSchema && <script type="application/ld+json">{JSON.stringify(eventSchema)}</script>}
      </Helmet>

      {/* live weather — very, very top of the page */}
      <div data-testid="month-live-weather" className="bg-white border-b border-zinc-200">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-2.5">
          <WeatherBadge placeholder />
        </div>
      </div>

      {/* month switcher — very top of the page */}
      <section data-testid="month-switcher" className="bg-white">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-8 md:py-10">
          <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-6">
            <p className="font-body font-800 text-xs tracking-[0.3em] eyebrow text-[#DF3226]">WHEN ARE YOU COMING?</p>
            <div className="flex gap-3">
              <Link
                to={`/copenhagen-in/${prev.slug}`}
                data-testid="month-prev"
                className="inline-flex items-center gap-1.5 font-body font-700 text-sm text-black/70 hover:text-[#DF3226] transition-colors"
              >
                <ArrowLeft size={16} /> {prev.name}
              </Link>
              <Link
                to={`/copenhagen-in/${next.slug}`}
                data-testid="month-next"
                className="inline-flex items-center gap-1.5 font-body font-700 text-sm text-black/70 hover:text-[#DF3226] transition-colors"
              >
                {next.name}
              </Link>
            </div>
          </div>
          <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2">
            {MONTH_PAGES.map((x) => (
              <Link
                key={x.slug}
                to={`/copenhagen-in/${x.slug}`}
                data-testid={`month-switch-${x.slug}`}
                aria-current={x.slug === m.slug ? "page" : undefined}
                className={`text-center font-display font-800 text-sm md:text-base border px-3 py-2.5 transition-all ${
                  x.slug === m.slug
                    ? "bg-[#DF3226] text-white border-[#DF3226] shadow-brut"
                    : "bg-[#F9F8F6] border-zinc-200 hover:border-black hover:-translate-y-0.5"
                }`}
              >
                {x.name}
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* hero + weather */}
      <section data-testid="month-hero" className="bg-[#F9F8F6] border-t border-dotted border-zinc-300">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-12 md:py-20">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
            <div className="lg:col-span-7">
              <Reveal>
                <div className="flex items-center gap-3 mb-4">
                  <p className="font-body font-800 text-xs tracking-[0.3em] eyebrow text-[#DF3226]">
                    {m.kicker.toUpperCase()}
                  </p>
                </div>
                <h1 className="font-display font-900 tracking-tighter leading-[0.9] text-4xl sm:text-5xl lg:text-6xl">
                  Copenhagen in <span className="">{m.name}</span>
                </h1>
                <p className="font-editorial italic font-600 text-[#DF3226] text-xl md:text-2xl mt-4">{m.lead}</p>
                <p className="font-body text-base md:text-lg text-black/70 mt-6 max-w-xl">{m.intro}</p>
              </Reveal>

              <div className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div data-testid="month-stat-temp" className="bg-white border border-zinc-200 shadow-brut p-4">
                  <div className="flex items-center gap-2 text-[#DF3226]">
                    <Thermometer size={16} />
                    <p className="font-body font-800 text-[10px] tracking-[0.25em] text-black/40">TYPICAL TEMPS</p>
                  </div>
                  <p className="font-display font-900 text-2xl tracking-tight mt-2">{w.temp}</p>
                  <p data-testid="month-weather-note" className="font-body text-xs text-black/50 mt-1.5 italic leading-snug">{w.note}</p>
                </div>
                <div data-testid="month-stat-daylight" className="bg-white border border-zinc-200 shadow-brut p-4">
                  <div className="flex items-center gap-2 text-[#DF3226]">
                    <Sun size={16} />
                    <p className="font-body font-800 text-[10px] tracking-[0.25em] text-black/40">DAYLIGHT</p>
                  </div>
                  <p className="font-display font-900 text-2xl tracking-tight mt-2">{m.daylight}</p>
                  {m.daylightNote && (
                    <p data-testid="month-daylight-note" className="font-body text-xs text-black/50 mt-1 italic">{m.daylightNote}</p>
                  )}
                </div>
                <div data-testid="month-stat-pack" className="bg-white border border-zinc-200 shadow-brut p-4 sm:col-span-1">
                  <div className="flex items-center gap-2 text-[#DF3226]">
                    <Backpack size={16} />
                    <p className="font-body font-800 text-[10px] tracking-[0.25em] text-black/40">PACK</p>
                  </div>
                  <p className="font-body font-700 text-sm leading-snug mt-2">{w.pack}</p>
                </div>
              </div>
              {m.num >= 5 && m.num <= 8 && (
              <div
                data-testid="month-weather-disclaimer"
                className="mt-6 max-w-xl rounded-2xl bg-white p-5 border-2 border-dashed border-black/15"
              >
                <div className="flex items-center gap-2 text-[#DF3226]">
                  <CloudRain size={18} />
                  <span className="font-body font-800 text-xs tracking-[0.25em]">A LITTLE DISCLAIMER</span>
                </div>
                <p className="font-body text-sm md:text-base text-black/70 mt-3 leading-relaxed">
                  After all, this is Scandinavia — the weather doesn't always read the script. Whatever the
                  month, don't be surprised if a day (or three) turns grey and rainy. Pack a light layer and
                  a compact umbrella, just in case.
                </p>
              </div>
              )}
            </div>

            <div className="lg:col-span-5">
              <Reveal delay={0.08}>
                <div className="border border-zinc-200 shadow-brut overflow-hidden aspect-[4/3]">
                  <img src={m.img} alt={m.imgAlt} className="w-full h-full object-cover" />
                </div>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      {/* what's on */}
      <section data-testid="month-events" className="bg-white border-t border-dotted border-zinc-300">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-12 md:py-20">
          <Reveal>
            <div className="flex items-center gap-3 mb-4">
              <p className="font-body font-800 text-xs tracking-[0.3em] eyebrow text-[#DF3226]">WHAT&apos;S ON</p>
            </div>
            <h2 className="font-display font-900 tracking-tighter leading-[0.9] text-3xl sm:text-4xl lg:text-5xl">
              {m.name}, <span className="font-editorial italic font-600">marked up</span>
            </h2>
          </Reveal>
          <div className="mt-10 border-t border-dotted border-zinc-300">
            {events.map((e, i) => (
              <Reveal key={e.name} delay={i * 0.03}>
                <div
                  data-testid={`month-event-${i}`}
                  className="grid grid-cols-12 gap-4 items-center border-b border-zinc-200 py-5 px-2 hover:bg-[#F9F8F6] transition-colors"
                >
                  <span className="col-span-12 sm:col-span-3 md:col-span-2 font-display font-900 text-sm md:text-base bg-[#DF3226] text-white border border-zinc-200 px-2 py-1 text-center">
                    {e.when}
                  </span>
                  <div className="col-span-12 sm:col-span-9 md:col-span-10">
                    <h3 className="font-display font-800 tracking-tight text-lg md:text-2xl leading-none">
                      {e.url ? (
                        <a
                          href={e.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          data-testid={`month-event-link-${i}`}
                          className="inline-flex items-center gap-1.5 hover: transition-colors"
                        >
                          {e.name} <ArrowUpRight size={18} className="shrink-0" />
                        </a>
                      ) : e.to ? (
                        <Link
                          to={e.to}
                          data-testid={`month-event-link-${i}`}
                          className="inline-flex items-center gap-1.5 hover: transition-colors"
                        >
                          {e.name} <ArrowUpRight size={18} className="shrink-0" />
                        </Link>
                      ) : (
                        e.name
                      )}
                    </h3>
                    <p className="font-body text-sm text-black/70 mt-1.5 max-w-2xl">{e.note}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
          {/* compact Tivoli ticket ad — months where the gardens are a headline act */}
          {m.tivoliAd && (
            <Reveal delay={0.05}>
              <div className="mt-8 max-w-md">
                <GygTivoliCard testid={`month-tivoli-ticket-${m.slug}`} />
              </div>
            </Reveal>
          )}
        </div>
      </section>

      {/* do this */}
      <section data-testid="month-dos" className="bg-[#F9F8F6] border-t border-dotted border-zinc-300">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-12 md:py-20">
          <Reveal>
            <div className="flex items-center gap-3 mb-4">
              <p className="font-body font-800 text-xs tracking-[0.3em] eyebrow text-[#DF3226]">DO THIS IN {m.name.toUpperCase()}</p>
            </div>
            <h2 className="font-display font-900 tracking-tighter leading-[0.9] text-3xl sm:text-4xl lg:text-5xl">
              Make the most of it<span className="">.</span>
            </h2>
          </Reveal>
          <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-4">
            {m.dos.map((d, i) => (
              <Reveal key={d.title} delay={i * 0.04}>
                <div data-testid={`month-do-${i}`} className="h-full bg-white border border-zinc-200 shadow-brut p-6 hover:-translate-y-0.5 hover:shadow-xl transition-all">
                  <h3 className="font-display font-800 tracking-tight text-xl leading-tight">{d.title}</h3>
                  <p className="font-body text-sm text-black/70 mt-2.5 leading-relaxed">{d.body}</p>
                  {d.link && (
                    <Link
                      to={d.link.to}
                      data-testid={`month-do-link-${i}`}
                      className="mt-4 inline-flex items-center gap-1.5 font-body font-800 text-sm text-[#DF3226] hover:text-black transition-colors"
                    >
                      {d.link.label}
                      <ArrowUpRight size={16} className="group-hover:translate-x-0.5" />
                    </Link>
                  )}
                  {d.ticket && (
                    <a
                      href={d.ticket}
                      target="_blank"
                      rel="noopener noreferrer sponsored"
                      data-testid={`month-do-ticket-${i}`}
                      className="mt-4 ml-4 inline-flex items-center gap-1.5 font-body font-800 text-sm text-[#DF3226] hover:text-black transition-colors"
                    >
                      Tickets
                      <ArrowUpRight size={16} className="group-hover:translate-x-0.5" />
                    </a>
                  )}
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* planner CTA */}
      <section className="bg-[#13141A] text-white border-t border-dotted border-zinc-300">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-16 md:py-24 text-center md:text-left flex flex-col md:flex-row items-center justify-between gap-8">
          <h2 className="font-display font-900 tracking-tighter leading-[0.9] text-4xl md:text-5xl">
            Coming in {m.name}?<br />
            <span className="font-editorial italic font-600">Build your days around it</span>
          </h2>
          <Link
            to={`/planner?month=${m.num}`}
            data-testid="month-planner-cta"
            className="inline-flex items-center gap-2 font-display font-900 text-lg bg-[#DF3226] text-white px-8 py-4 rounded-full shadow-brut hover:-translate-y-1 hover:shadow-brut-lg transition-all duration-300"
          >
            Plan your {m.name} trip
          </Link>
        </div>
      </section>

      {/* key forces a full remount per tour — without it React reuses the widget
          div on month switch and GYG's iframe keeps showing the previous tour */}
      <GygTourBand key={tour.tourId} tourId={tour.tourId} label={tour.label} />

      <ExploreLinks testid="explore-month" links={m.explore} />

      {/* faq — last content section */}
      <section data-testid="month-faq" className="bg-[#F9F8F6] border-t border-dotted border-zinc-300">
        <div className="max-w-[1000px] mx-auto px-5 md:px-10 py-12 md:py-20">
          <p className="font-body font-700 text-xs uppercase tracking-[0.3em] eyebrow text-[#DF3226] mb-4">Good to know</p>
          <h2 className="font-display font-900 tracking-tighter leading-[0.95] text-3xl sm:text-4xl lg:text-5xl">
            {m.name}, <span className="">answered.</span>
          </h2>
          <div className="mt-8 divide-y divide-zinc-200 border-y border-dotted border-zinc-300">
            {m.faq.map((f, i) => (
              <details key={f.q} data-testid={`month-faq-${i}`} className="group py-5">
                <summary className="flex items-start justify-between gap-4 cursor-pointer list-none font-display font-800 text-lg text-black">
                  <span>{f.q}</span>
                  <Plus size={22} className="shrink-0 mt-1 text-[#DF3226] transition-transform duration-300 group-open:rotate-45" />
                </summary>
                <p className="font-body text-base text-black/70 mt-3 max-w-3xl leading-relaxed">{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      <ShareBar title={title} />
    </main>
  );
}
